// Client-side image prewarming for the media gallery. The goal: by the time a
// traveler opens the lightbox or taps prev/next, the bytes are already in the
// browser cache AND decoded off the main thread — so the swap paints instantly
// with no flash. Pairs with `variantUrlForWidth` so we prewarm exactly the URL
// next/image will later request.
//
// "Prerender" (Speculation Rules) only applies to whole-page navigations, not
// in-page modal images; preload + `HTMLImageElement.decode()` is the in-page
// equivalent.

import { LARGE_WIDTH, SMALL_WIDTH, variantUrlForWidth } from "./image-variants";

interface NetworkInformationLite {
  saveData?: boolean;
  effectiveType?: string;
}

/** Skip aggressive (whole-gallery) warming on data-saver or slow links. */
function aggressivePrefetchAllowed(): boolean {
  if (typeof navigator === "undefined") return false;
  const conn = (
    navigator as Navigator & { connection?: NetworkInformationLite }
  ).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  if (conn.effectiveType && /(slow-2g|2g|3g)/.test(conn.effectiveType))
    return false;
  return true;
}

const inflight = new Set<string>();

/** Fetch + decode a single URL off the main thread. Idempotent; best-effort. */
function warm(url: string): Promise<void> {
  if (!url || inflight.has(url) || typeof window === "undefined")
    return Promise.resolve();
  inflight.add(url);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  const decoded =
    typeof img.decode === "function" ? img.decode() : Promise.resolve();
  return decoded.then(
    () => undefined,
    () => undefined,
  );
}

export function prefetchLarge(url: string): Promise<void> {
  return warm(variantUrlForWidth(url, LARGE_WIDTH));
}

function prefetchThumb(url: string): Promise<void> {
  return warm(variantUrlForWidth(url, SMALL_WIDTH));
}

/** Warm what the lightbox shows first: the opening image (large) + the
 *  filmstrip thumbnails that will be on screen. Cheap enough to run on hover
 *  intent regardless of connection. */
export function prewarmGalleryOpen(urls: string[], firstIndex = 0): void {
  if (urls.length === 0) return;
  void prefetchLarge(urls[firstIndex] ?? urls[0]!);
  for (const u of urls.slice(0, 12)) void prefetchThumb(u);
}

/** Progressively warm every `large` variant during idle time, capped
 *  concurrency, gated on connection. Returns a cancel fn. */
export function idlePrefetchLargeAll(
  urls: string[],
  concurrency = 2,
): () => void {
  if (!aggressivePrefetchAllowed() || typeof window === "undefined")
    return () => {};
  const queue = [...urls];
  let cancelled = false;
  const schedule: (cb: () => void) => void =
    "requestIdleCallback" in window
      ? (cb) => window.requestIdleCallback(cb)
      : (cb) => void window.setTimeout(cb, 300);

  const pump = (): void => {
    if (cancelled) return;
    const batch: Promise<void>[] = [];
    for (let i = 0; i < concurrency && queue.length > 0; i++) {
      batch.push(prefetchLarge(queue.shift()!));
    }
    if (batch.length === 0) return;
    void Promise.all(batch).then(() => {
      if (!cancelled) schedule(pump);
    });
  };
  schedule(pump);
  return () => {
    cancelled = true;
  };
}
